import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Pagination,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { auditApi, AuditLog, AuditStatistics } from '../../api/admin';

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [statistics, setStatistics] = useState<AuditStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadAuditLogs();
    loadStatistics();
  }, [page, pageSize]);

  const loadAuditLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {
        page,
        size: pageSize,
      };
      
      if (userId) params.userId = userId;
      if (action) params.action = action;
      if (entityType) params.entityType = entityType;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const response = await auditApi.getAuditLogs(params);
      setLogs(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await auditApi.getAuditStatistics();
      setStatistics(stats);
    } catch (err: any) {
      console.error('Failed to load audit statistics:', err);
    }
  };

  const handleSearch = () => {
    setPage(0); // Reset to first page
    loadAuditLogs();
  };

  const handleClearFilters = () => {
    setUserId('');
    setAction('');
    setEntityType('');
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1); // MUI Pagination is 1-based, API is 0-based
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatDateForExport = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const exportToPDF = () => {
    import('jspdf').then((jsPDFModule) => {
      const { jsPDF } = jsPDFModule;
      const doc = new jsPDF('landscape');
      
      doc.setFontSize(16);
      doc.text('Audit Logs Report', 14, 15);
      
      if (startDate || endDate) {
        doc.setFontSize(10);
        doc.text(
          `Date Range: ${startDate || 'All'} to ${endDate || 'All'}`,
          14,
          22
        );
      }
      
      doc.setFontSize(8);
      doc.text(`Exported: ${new Date().toLocaleString()}`, 14, 28);
      
      const headers = ['Timestamp', 'User', 'Action', 'Entity', 'Status', 'IP Address', 'Details'];
      const startY = 35;
      let currentY = startY;
      
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      const colWidths = [30, 25, 20, 20, 15, 20, 50];
      let xPos = 14;
      
      headers.forEach((header, index) => {
        doc.text(header, xPos, currentY);
        xPos += colWidths[index];
      });
      
      currentY += 5;
      doc.line(14, currentY, 280, currentY);
      currentY += 3;
      
      doc.setFont(undefined, 'normal');
      doc.setFontSize(7);
      
      logs.forEach((log, index) => {
        if (currentY > 190) {
          doc.addPage();
          currentY = 20;
          
          doc.setFont(undefined, 'bold');
          doc.setFontSize(9);
          xPos = 14;
          headers.forEach((header, idx) => {
            doc.text(header, xPos, currentY);
            xPos += colWidths[idx];
          });
          currentY += 5;
          doc.line(14, currentY, 280, currentY);
          currentY += 3;
          doc.setFont(undefined, 'normal');
          doc.setFontSize(7);
        }
        
        const row = [
          formatDateForExport(log.timestamp || log.createdAt),
          log.username || log.userId?.substring(0, 15) || 'N/A',
          log.action || 'N/A',
          log.entityType && log.entityId ? `${log.entityType} #${log.entityId}` : 'N/A',
          log.success !== false ? 'Success' : 'Failed',
          log.ipAddress || 'N/A',
          (log.details || log.errorMessage || 'N/A').substring(0, 60)
        ];
        
        xPos = 14;
        row.forEach((cell, cellIndex) => {
          const cellText = String(cell || 'N/A');
          const lines = doc.splitTextToSize(cellText, colWidths[cellIndex] - 2);
          doc.text(lines, xPos, currentY);
          xPos += colWidths[cellIndex];
        });
        
        currentY += Math.max(...row.map((cell, idx) => {
          const cellText = String(cell || 'N/A');
          const lines = doc.splitTextToSize(cellText, colWidths[idx] - 2);
          return lines.length * 4;
        })) + 2;
      });
      
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(
          `Page ${i} of ${pageCount}`,
          280 - doc.getTextWidth(`Page ${i} of ${pageCount}`),
          200
        );
      }
      
      const fileName = `audit-logs-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    }).catch((error) => {
      console.error('Failed to export PDF:', error);
      alert('Failed to export PDF. Please install jsPDF library: npm install jspdf');
    });
  };

  const exportToExcel = async () => {
    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      
      const worksheet = workbook.addWorksheet('Audit Logs');
      
      worksheet.columns = [
        { header: 'Timestamp', key: 'timestamp', width: 20 },
        { header: 'User ID', key: 'userId', width: 30 },
        { header: 'Username', key: 'username', width: 20 },
        { header: 'Action', key: 'action', width: 15 },
        { header: 'Entity Type', key: 'entityType', width: 15 },
        { header: 'Entity ID', key: 'entityId', width: 12 },
        { header: 'Status', key: 'status', width: 12 },
        { header: 'IP Address', key: 'ipAddress', width: 15 },
        { header: 'Description', key: 'description', width: 50 },
        { header: 'Error Message', key: 'errorMessage', width: 50 },
        { header: 'Request Method', key: 'requestMethod', width: 15 },
        { header: 'Request URI', key: 'requestUri', width: 40 },
        { header: 'User Agent', key: 'userAgent', width: 40 },
      ];
      
      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
      };
      
      logs.forEach((log) => {
        worksheet.addRow({
          timestamp: formatDateForExport(log.timestamp || log.createdAt),
          userId: log.userId || 'N/A',
          username: log.username || 'N/A',
          action: log.action || 'N/A',
          entityType: log.entityType || 'N/A',
          entityId: log.entityId || 'N/A',
          status: log.success !== false ? 'Success' : 'Failed',
          ipAddress: log.ipAddress || 'N/A',
          description: log.details || 'N/A',
          errorMessage: log.errorMessage || '',
          requestMethod: log.requestMethod || 'N/A',
          requestUri: log.requestUri || 'N/A',
          userAgent: log.userAgent ? log.userAgent.substring(0, 100) : 'N/A',
        });
      });
      
      const metadataSheet = workbook.addWorksheet('Export Info');
      metadataSheet.columns = [
        { header: 'Property', key: 'property', width: 25 },
        { header: 'Value', key: 'value', width: 50 },
      ];
      
      metadataSheet.getRow(1).font = { bold: true };
      metadataSheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
      };
      
      metadataSheet.addRows([
        { property: 'Export Date', value: new Date().toLocaleString() },
        { property: 'Total Records', value: totalElements },
        { property: 'Filtered Records', value: logs.length },
        { property: 'User ID Filter', value: userId || 'All' },
        { property: 'Action Filter', value: action || 'All' },
        { property: 'Entity Type Filter', value: entityType || 'All' },
        { property: 'Start Date', value: startDate || 'All' },
        { property: 'End Date', value: endDate || 'All' },
      ]);
      
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Excel:', error);
      alert('Failed to export Excel. Please ensure exceljs library is installed.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Audit Logs
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportToPDF}
            disabled={loading || logs.length === 0}
            color="error"
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableChartIcon />}
            onClick={exportToExcel}
            disabled={loading || logs.length === 0}
            color="success"
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Logs
                </Typography>
                <Typography variant="h4">{statistics.totalLogs.toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Successful Actions
                </Typography>
                <Typography variant="h4" color="success.main">
                  {statistics.successCount.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Failed Actions
                </Typography>
                <Typography variant="h4" color="error.main">
                  {statistics.failureCount.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Success Rate
                </Typography>
                <Typography variant="h4">
                  {statistics.totalLogs > 0
                    ? ((statistics.successCount / statistics.totalLogs) * 100).toFixed(1)
                    : 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Action</InputLabel>
              <Select
                value={action}
                label="Action"
                onChange={(e) => setAction(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="LOGIN">Login</MenuItem>
                <MenuItem value="LOGOUT">Logout</MenuItem>
                <MenuItem value="CREATE_LOAN">Create Loan</MenuItem>
                <MenuItem value="UPDATE_LOAN">Update Loan</MenuItem>
                <MenuItem value="WITHDRAW_LOAN">Withdraw Loan</MenuItem>
                <MenuItem value="CREATE_PAYMENT">Create Payment</MenuItem>
                <MenuItem value="UPDATE_PAYMENT">Update Payment</MenuItem>
                <MenuItem value="CREATE_ZIG_RATE">Create ZIG Rate</MenuItem>
                <MenuItem value="UPDATE_ZIG_RATE">Update ZIG Rate</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Entity Type</InputLabel>
              <Select
                value={entityType}
                label="Entity Type"
                onChange={(e) => setEntityType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="LOAN">Loan</MenuItem>
                <MenuItem value="PAYMENT">Payment</MenuItem>
                <MenuItem value="USER">User</MenuItem>
                <MenuItem value="ZIG_RATE">ZIG Rate</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Start Date"
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="End Date"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                fullWidth
              >
                Search
              </Button>
              <Tooltip title="Refresh">
                <IconButton onClick={loadAuditLogs} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
        {(userId || action || entityType || startDate || endDate) && (
          <Box sx={{ mt: 2 }}>
            <Button size="small" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Audit Logs Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No audit logs found</Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>{formatDate(log.timestamp || log.createdAt)}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{log.username || 'N/A'}</Typography>
                        {log.userId && (
                          <Typography variant="caption" color="text.secondary">
                            {log.userId.substring(0, 8)}...
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={log.action} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {log.entityType && log.entityId ? (
                        <Typography variant="body2">
                          {log.entityType} #{log.entityId}
                        </Typography>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {log.success !== false ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Success"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          icon={<ErrorIcon />}
                          label="Failed"
                          size="small"
                          color="error"
                        />
                      )}
                    </TableCell>
                    <TableCell>{log.ipAddress || 'N/A'}</TableCell>
                    <TableCell>
                      <Tooltip title={log.details || log.errorMessage || ''}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {log.details || log.errorMessage || 'N/A'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {logs.length} of {totalElements} logs
              </Typography>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Page Size</InputLabel>
                <Select
                  value={pageSize}
                  label="Page Size"
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(0);
                  }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
        )}
      </TableContainer>
    </Box>
  );
};

export default AuditLogs;

